/**
 * Slack notifications seam. Same pattern as meetNotes.ts:
 * - When VITE_SLACK_API_URL is configured, post to the real worker.
 * - Otherwise, a deterministic local simulation so the feature works offline.
 *
 * No secrets in the client. Real Slack OAuth access lives behind the worker.
 *
 *   POST {base}/notify  { text }  → { ok: boolean; simulated: boolean }
 */

const BASE_URL: string | undefined = (
  import.meta.env.VITE_SLACK_API_URL as string | undefined
)?.replace(/\/+$/, '');
const API_KEY = import.meta.env.VITE_SLACK_API_KEY as string | undefined;

const TIMEOUT_MS = 15_000;

/** True when a real Slack endpoint is configured. */
export function isConfigured(): boolean {
  return Boolean(BASE_URL);
}

/** Backend descriptor for status pills / debugging. */
export function slackInfo(): { configured: boolean; baseUrl: string | null } {
  return { configured: isConfigured(), baseUrl: BASE_URL ?? null };
}

export interface SlackNotifyResponse {
  ok: boolean;
  simulated: boolean;
}

/**
 * Post a text message to Slack. Never throws — a worker/network failure
 * downgrades to a local simulation so the action always completes.
 */
export async function postToSlack(
  text: string,
): Promise<SlackNotifyResponse> {
  if (isConfigured()) {
    try {
      return await postSlackMessage(text);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Argo slack] worker failed, using local simulation:', err);
    }
  }
  return simulateSlackPost(text);
}

async function postSlackMessage(text: string): Promise<SlackNotifyResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE_URL}/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
      },
      body: JSON.stringify({ text }),
      signal: controller.signal,
    });
    if (!res.ok) {
      throw new Error(
        `Slack worker responded ${res.status} ${res.statusText}`,
      );
    }
    const raw = (await res.json()) as { ok?: boolean };
    return { ok: Boolean(raw.ok), simulated: false };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Deterministic local simulation: always succeeds, no side effects.
 * Used in local dev and when the worker endpoint is not configured.
 */
function simulateSlackPost(text: string): SlackNotifyResponse {
  // Deterministic: just log and resolve.
  // eslint-disable-next-line no-console
  console.log('[Argo slack] simulated post:', text.slice(0, 80));
  return { ok: true, simulated: true };
}
